import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { CheckCircle, Loader2, Search } from "lucide-react";
import { PERSON_LOCATION_TYPES, type PersonLocationType } from "@releve/shared";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  StandardDialog,
  StandardDialogBody,
  StandardDialogContent,
  StandardDialogDescription,
  StandardDialogFooter,
  StandardDialogHeader,
  StandardDialogTitle,
} from "@/components/ui/standard-dialog";
import { useAddressLookup } from "@/hooks/useAddressLookup";
import { useTranslation } from "@/hooks/useTranslation";
import { toast } from "@/hooks/useToast";
import { captureException } from "@/lib/sentry";

type AddressSuggestion = ReturnType<typeof useAddressLookup>["searchResults"][number];

const locationSchema = z.object({
  location_type: z.enum(PERSON_LOCATION_TYPES),
  notes: z.string(),
});

type LocationFormValues = z.infer<typeof locationSchema>;

/** What the dialog hands back: a saved `gis_locations` row plus the link fields. */
export interface AddedPersonLocation {
  gis_location_id: string;
  formatted_address: string;
  location_type: PersonLocationType;
  notes: string;
}

interface AddPersonLocationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAdd: (location: AddedPersonLocation) => void;
}

/**
 * Search, validate and geocode an address, save it to `gis_locations`
 * (resolve-or-create, `useAddressLookup().saveLocation`), and hand the new
 * location back to the person form. The person's `person_locations` link is
 * written with the rest of the form on save. Mount it only while open.
 */
export function AddPersonLocationDialog({ open, onOpenChange, onAdd }: AddPersonLocationDialogProps) {
  const { t } = useTranslation("people");
  const [addressInput, setAddressInput] = useState("");
  const [selectedSuggestion, setSelectedSuggestion] = useState<AddressSuggestion | null>(null);
  const [addressError, setAddressError] = useState<string | null>(null);
  const [isResolving, setIsResolving] = useState(false);

  const { setSearchQuery, searchResults, isSearching, geocodeAddress, saveLocation, isSaving, formatAddress } =
    useAddressLookup({ autoValidate: true, cacheResults: true });

  const form = useForm<LocationFormValues>({
    resolver: zodResolver(locationSchema),
    defaultValues: { location_type: "home", notes: "" },
  });

  const handleAddressChange = (query: string) => {
    setAddressInput(query);
    setSearchQuery(query);
    setSelectedSuggestion(null);
    setAddressError(null);
  };

  const handleSuggestionSelect = (suggestion: AddressSuggestion) => {
    setSelectedSuggestion(suggestion);
    setAddressInput(formatAddress(suggestion.address));
    setSearchQuery("");
    setAddressError(null);
  };

  const onSubmit = async (values: LocationFormValues) => {
    if (!selectedSuggestion && !addressInput.trim()) {
      setAddressError(t("form.location.addressRequired"));
      return;
    }
    setIsResolving(true);
    try {
      let resolved: Pick<AddressSuggestion, "address" | "coordinates" | "confidence" | "confidenceSource" | "placeId"> | null =
        selectedSuggestion;
      // No suggestion picked: geocode the raw input.
      if (!resolved) {
        const [result] = await geocodeAddress(addressInput.trim());
        if (result) {
          resolved = {
            address: result.address,
            coordinates: { latitude: result.position.lat, longitude: result.position.lon },
            confidence: result.score,
            confidenceSource: "match",
            placeId: result.id,
          };
        }
      }
      if (!resolved) {
        setAddressError(t("form.location.notFound"));
        return;
      }

      const gisLocationId = await saveLocation({
        address: resolved.address,
        coordinates: resolved.coordinates,
        originalInput: addressInput,
        confidence: resolved.confidence,
        confidenceSource: resolved.confidenceSource,
        placeId: resolved.placeId,
      });

      onAdd({
        gis_location_id: gisLocationId,
        formatted_address: resolved.address.formattedAddress || formatAddress(resolved.address),
        location_type: values.location_type,
        notes: values.notes.trim(),
      });
      onOpenChange(false);
    } catch (error) {
      captureException(error as Error, { context: "AddPersonLocationDialog.add" });
      toast.error(t("form.location.addFailed"));
    } finally {
      setIsResolving(false);
    }
  };

  const busy = isResolving || isSaving;

  return (
    <StandardDialog open={open} onOpenChange={onOpenChange}>
      <StandardDialogContent size="md">
        <StandardDialogHeader>
          <StandardDialogTitle>{t("form.location.title")}</StandardDialogTitle>
          <StandardDialogDescription>{t("form.location.description")}</StandardDialogDescription>
        </StandardDialogHeader>
        <Form {...form}>
          <form
            className="contents"
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              void form.handleSubmit(onSubmit)(e);
            }}
          >
            <StandardDialogBody className="space-y-4">
              <FormField
                control={form.control}
                name="location_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("form.location.type")}</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {PERSON_LOCATION_TYPES.map((type) => (
                          <SelectItem key={type} value={type}>
                            {t(`common:people.locationTypes.${type}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormItem>
                )}
              />

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Label htmlFor="person-location-address">{t("form.location.address")}</Label>
                  {addressError && <span className="text-xs text-destructive">{addressError}</span>}
                </div>
                <div className="relative">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="person-location-address"
                    placeholder={t("form.location.addressPlaceholder")}
                    value={addressInput}
                    onChange={(e) => handleAddressChange(e.target.value)}
                    className="pl-9"
                    autoComplete="off"
                  />
                  {isSearching && (
                    <Loader2 className="absolute right-3 top-3 h-4 w-4 animate-spin text-muted-foreground" />
                  )}
                </div>

                {searchResults.length > 0 && !selectedSuggestion && (
                  <ul className="border rounded-lg max-h-48 overflow-y-auto">
                    {searchResults.map((result, index) => (
                      <li key={result.placeId ?? index} className="border-b last:border-0">
                        <button
                          type="button"
                          className="w-full text-left p-3 text-sm hover:bg-muted"
                          onClick={() => handleSuggestionSelect(result)}
                        >
                          {formatAddress(result.address)}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}

                {selectedSuggestion && (
                  <div className="border rounded-lg p-3 bg-muted">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      <CheckCircle className="h-4 w-4 text-success" />
                      {t("form.location.selected")}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">{formatAddress(selectedSuggestion.address)}</p>
                  </div>
                )}
              </div>

              <FormField
                control={form.control}
                name="notes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("form.location.notes")}</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder={t("form.location.notesPlaceholder")} />
                    </FormControl>
                  </FormItem>
                )}
              />
            </StandardDialogBody>
            <StandardDialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                {t("common:cancel")}
              </Button>
              <Button type="submit" disabled={busy}>
                {busy ? t("form.location.adding") : t("form.location.add")}
              </Button>
            </StandardDialogFooter>
          </form>
        </Form>
      </StandardDialogContent>
    </StandardDialog>
  );
}
